import { useStripe } from "@stripe/react-stripe-js";
import type {
  ConfirmKlarnaPaymentData,
  CreatePaymentMethodKlarnaData,
  PaymentIntent,
} from "@stripe/stripe-js";
import { t } from "ttag";

import type { Price } from "@/shared/domains";
import { BILLING_FREQUENCY } from "@/shared/domains";

const KLARNA_AVAILABLE_BILLING_FREQUENCIES = [
  BILLING_FREQUENCY.QUARTERLY,
  BILLING_FREQUENCY.SEMI_ANNUALLY,
  BILLING_FREQUENCY.ANNUALLY,
] as const;

interface PayWithKlarnaParams {
  clientSecret: NonNullable<PaymentIntent["client_secret"]>;
  billingDetails: CreatePaymentMethodKlarnaData["billing_details"];
  returnUrl: NonNullable<ConfirmKlarnaPaymentData["return_url"]>;
}

interface UseKlarnaParams {
  price?: Price;
}

export const useKlarna = ({ price }: UseKlarnaParams) => {
  const stripe = useStripe();

  const available = KLARNA_AVAILABLE_BILLING_FREQUENCIES.some(
    (frequency) => frequency === price?.durationInMonths,
  );

  const pay = async ({
    clientSecret,
    billingDetails,
    returnUrl,
  }: PayWithKlarnaParams) => {
    if (!stripe) {
      throw new Error(t`Something went wrong. Please try again later.`);
    }

    await stripe?.confirmKlarnaPayment(clientSecret, {
      payment_method: {
        billing_details: billingDetails,
      },
      return_url: returnUrl,
    });
  };

  return { pay, available };
};
